import { Flex, SimpleGrid, TextInput, Title } from "@mantine/core";
import { useDebouncedState } from "@mantine/hooks";
import { orderBy, sumBy, toLower } from "lodash";
import get from "lodash/get";
import groupBy from "lodash/groupBy";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useMemo, useState } from "react";

import { useCompanyOptions } from "../../../api/hooks/customer/useCompanyOptions.ts";
import { useTradeInsurances } from "../../../api/hooks/tradeInsurance/useTradeInsurances.ts";
import { useTradeInsuranceStatistics } from "../../../api/hooks/tradeInsurance/useTradeInsuranceStatistics.ts";
import SingleFieldCard from "../../../components/Cards/SingleFieldCard/SingleFieldCard.tsx";
import { MultiSelect } from "../../../components/MultiSelect.tsx";
import { Select } from "../../../components/Select.tsx";
import Table from "../../../components/Table/Table.tsx";
import { TradeInsuranceStatus } from "../../../graphql/generated.ts";
import {
  TradeInsurance,
  tradeInsuranceStatusOptions,
} from "../../../types/tradeInsurance.ts";
import { getCompanyOptions } from "../../../utils/company.ts";
import { toCurrencyPrice } from "../../../utils/number.ts";
import { FiltersValue } from "../../TradeInsurance/types.ts";
import { getTradeInsuranceApiFilter } from "../../TradeInsurance/utils.ts";
import { useTradeInsuranceDashboardTableData } from "./hooks/useTradeInsuranceDashboardTableData.tsx";
import { TradeInsuranceDashboardItem } from "./types.ts";

export default function TradeInsuranceDashboard() {
  const [insuranceName, setInsuranceName] = useDebouncedState("", 500);
  const [filter, setFilter] = useState<FiltersValue>({
    status: TradeInsuranceStatus.Approved,
  });
  const apiFilter = getTradeInsuranceApiFilter({ ...filter, insuranceName });

  const [order, setOrder] = useState<DataTableSortStatus>({
    columnAccessor: "insuranceName",
    direction: "asc",
  });

  const [{ data: statisticsResponse, fetching: fetchingStatistics }] =
    useTradeInsuranceStatistics({
      filter: apiFilter,
    });
  const tradeInsuranceStatistics = statisticsResponse?.tradeInsuranceStatistics;

  const [{ data: tradeInsurancesData, fetching: fetchingTradeInsurances }] =
    useTradeInsurances({
      filter: apiFilter,
    });
  const tradeInsurances: TradeInsuranceDashboardItem[] = useMemo(() => {
    const groupedData = groupBy(
      tradeInsurancesData?.tradeInsurances.data ?? [],
      (i) => `${toLower(i.insuranceName)}-${i.customer.id}`
    );

    const data = Object.keys(groupedData).map((key) => {
      const tradeInsurances: TradeInsurance[] = groupedData[key] ?? [];
      return {
        insuranceName: tradeInsurances[0].insuranceName,
        customerName: tradeInsurances[0].customer.name,
        tradeInsuranceAmount: sumBy(tradeInsurances, (i) =>
          Number(i.approvedAmount)
        ),
        totalLimitActive: sumBy(tradeInsurances, (i) => Number(i.limitAmount)),
        totalLimitUtilization: sumBy(tradeInsurances, (i) =>
          Number(i.limitUtilization)
        ),
        totalGiroAmount: sumBy(tradeInsurances, (i) => Number(i.giroAmount)),
        transactionsNumber: sumBy(tradeInsurances, "transactionsNumber"),
      };
    });
    return orderBy(data, (item) => toLower(get(item, order.columnAccessor)), [
      order.direction,
    ]);
  }, [
    order.columnAccessor,
    order.direction,
    tradeInsurancesData?.tradeInsurances.data,
  ]);

  const [columns, rows] = useTradeInsuranceDashboardTableData({
    data: tradeInsurances,
  });

  const [{ data: customersData, fetching: fetchingCustomers }] =
    useCompanyOptions();
  const companyOptions = getCompanyOptions(customersData?.customers.data);

  const handleFilterChange = useCallback(
    (key: keyof FiltersValue, value: unknown) => {
      setFilter({ [key]: value });
    },
    [setFilter]
  );

  return (
    <Flex direction="column" gap={{ base: 16, sm: 24 }}>
      <Title>Trade Insurance Dashboard</Title>
      <SimpleGrid cols={5} spacing={20} verticalSpacing={8}>
        <TextInput
          size="m"
          label="Insurance Name"
          placeholder="Insurance Name"
          onChange={(event) => {
            setInsuranceName(event.target.value);
          }}
        />
        <MultiSelect
          data={companyOptions}
          loading={fetchingCustomers}
          label="Customer Name"
          placeholder="Customer Name"
          size="m"
          searchable
          clearable
          onChange={(value: string[]) =>
            handleFilterChange("customerId", value)
          }
        />
        <Select
          size="m"
          clearable
          label="Status"
          placeholder="Status"
          data={tradeInsuranceStatusOptions}
          defaultValue={filter.status}
          onChange={(value: string) => handleFilterChange("status", value)}
        />
      </SimpleGrid>
      <SimpleGrid cols={5} spacing={20} verticalSpacing={8}>
        <SingleFieldCard
          label="# of Insured Customer"
          value={tradeInsuranceStatistics?.customersNumber ?? 0}
          loading={fetchingStatistics}
        />
        <SingleFieldCard
          label="Total Insurance Amount"
          value={toCurrencyPrice(
            tradeInsuranceStatistics?.totalInsuranceAmount
          )}
          loading={fetchingStatistics}
        />
        <SingleFieldCard
          label="Total Limit Active"
          value={toCurrencyPrice(tradeInsuranceStatistics?.totalLimitActive)}
          loading={fetchingStatistics}
        />
        <SingleFieldCard
          label="Total Limit Utilization"
          value={toCurrencyPrice(
            tradeInsuranceStatistics?.totalLimitUtilization
          )}
          loading={fetchingStatistics}
        />
        <SingleFieldCard
          label="Total Giro Amount"
          value={toCurrencyPrice(tradeInsuranceStatistics?.totalGiroAmount)}
          loading={fetchingStatistics}
        />
      </SimpleGrid>
      <Table
        loading={fetchingTradeInsurances}
        columns={columns}
        rows={rows}
        defaultSort={{ columnAccessor: "insuranceName", direction: "asc" }}
        onSortChange={setOrder}
      />
    </Flex>
  );
}
